import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import logoHorizontal from '../../assets/logo-horizontal.jpg';

const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const isAuthenticated = Boolean(user);

  const raw = user as Record<string, unknown> | null;
  const innerUser =
    raw && typeof raw === 'object' && 'user' in raw && raw.user && typeof raw.user === 'object'
      ? (raw.user as Record<string, unknown>)
      : raw;

  const displayName =
    innerUser && typeof innerUser.username === 'string'
      ? (innerUser.username as string)
      : innerUser && typeof innerUser.email === 'string'
        ? (innerUser.email as string)
        : '';

  const isAdmin =
    innerUser && typeof innerUser.role === 'string' && (innerUser.role as string) === 'admin';

  const isOnAdminPanel = location.pathname.startsWith('/admin');

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      navigate('/');
    }
  };

  const navLinkClass = (path: string) => {
    const active = location.pathname === path || location.pathname.startsWith(path + '/');
    return `text-sm font-medium px-2 py-1 rounded transition-colors ${active ? 'text-white bg-brand-accent' : 'text-white/80 hover:text-white hover:bg-brand-primary-hover'}`;
  };

  return (
    <header className="bg-brand-primary text-white">
      <div className="container mx-auto flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-4">
          <Link
            to="/"
            className="focus:outline-none focus:ring-2 focus:ring-white/70 rounded"
          >
            <img src={logoHorizontal} alt="TELLURVISION" className="h-9 w-auto" />
          </Link>
          {isAuthenticated && (
            <span className="text-sm text-white/80">Hi{displayName ? `, ${displayName}` : ''}</span>
          )}
        </div>
        {!isAuthenticated ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={toggleTheme}
              aria-label="Toggle dark mode"
              className="inline-flex items-center rounded-md border border-white/40 bg-brand-primary-hover px-2.5 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-brand-accent focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-primary"
            >
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>
            <Link
              to="/login"
              className="inline-flex items-center rounded-md border border-white/60 bg-brand-primary px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-brand-primary-hover focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-primary"
            >
              Login
            </Link>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={toggleTheme}
              aria-label="Toggle dark mode"
              className="inline-flex items-center rounded-md border border-white/40 bg-brand-primary-hover px-2.5 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-brand-accent focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-primary"
            >
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>
            {isAdmin && !isOnAdminPanel && (
              <Link to="/admin" className={navLinkClass('/admin')}>Admin</Link>
            )}
            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex items-center rounded-md border border-white/40 bg-brand-primary-hover px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-brand-accent focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-primary"
            >
              Logout
            </button>
          </div>
        )}
      </div>
      {isAuthenticated && (
        <nav className="border-t border-white/20">
          <div className="container mx-auto flex items-center gap-1 px-4 py-1.5 overflow-x-auto">
            <Link to="/campaigns" className={navLinkClass('/campaigns')}>My Campaigns</Link>
            <Link to="/ai-video" className={navLinkClass('/ai-video')}>AI Video</Link>
            <Link to="/ai-creative-studio" className={navLinkClass('/ai-creative-studio')}>AI Creative Studio</Link>
            <Link to="/billboard" className={navLinkClass('/billboard')}>Billboard</Link>
            <Link to="/account" className={navLinkClass('/account')}>My Account</Link>
          </div>
        </nav>
      )}
    </header>
  );
};

export default Header;
